import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  InputLabel,
} from "@material-ui/core";
import React, { useRef, useState, useEffect } from "react";
import makeClasses from "./styles";
import { useSelector, useDispatch } from "react-redux";
import { Constants, Language } from "../../utils";
import Button from "../Button";
import ContentBox from "../ContentBox";
import { toast } from "react-toastify";
import whatsAppIcon from "../../static/images/icons/social_whatsapp.svg";
import telegramIcon from "../../static/images/icons/social_telegram.svg";
import emailIcon from "../../static/images/icons/social_email.svg";
import { QRCode } from "react-qrcode-logo";
import handyQrLogo from "../../static/images/handy-qr-logo.svg";
import { PDFDocument } from "pdf-lib";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";
import { BranchesSelector } from "../../components";
import { business as businessActions } from "../../actions";

const SharePlanWithBranchesModal = ({
  open,
  onClose,
  link,
  shareText,
  branches,
}) => {
  const classes = makeClasses();
  const linkInputRef = useRef(null);
  const [step, setStep] = useState(branches?.length > 1 ? 1 : 2);
  const { languageTexts } = useSelector(({ language }) => ({
    languageTexts: language.texts || {},
  }));
  const i18n = Language(languageTexts);
  const [shareLink, setShareLink] = useState(link);
  const [selectedShareLinkBranch, setSelectedShareLinkBranch] = useState(
    branches?.length === 1 ? branches[0]?.traceId : "all"
  );
  const dispatch = useDispatch();

  useEffect(() => {
    setShareLink(link);
  }, [link]);

  const _copyLink = async () => {
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(shareLink);
        toast.success(i18n.get("Components.ShareModal.LinkCopied"));
      } catch (err) {
        console.error("Failed to copy text to clipboard.", err);
      }
    } else {
      linkInputRef.current.focus();
      linkInputRef.current.select();
      try {
        const successful = document.execCommand("copy");
        if (successful) {
          toast.success(i18n.get("Components.ShareModal.LinkCopied"));
        }
      } catch (err) {
        console.error("Fallback: Oops, unable to copy", err);
      }
    }
  };

  const downloadQR = async () => {
    const qrElement = document.querySelector("#react-qrcode-logo");
    const canvas = await html2canvas(qrElement);
    const qrImageData = canvas.toDataURL("image/png");
    const QR_PLANES_URL = "/QR_planes.pdf";
    const existingPdfBytes = await fetch(QR_PLANES_URL).then((res) =>
      res.arrayBuffer()
    );

    const pdfDoc = await PDFDocument.load(existingPdfBytes);

    const qrImage = await pdfDoc.embedPng(qrImageData);

    const pages = pdfDoc.getPages();

    const QRSide = 130;
    const alignmentAdjustment = 1;

    const firstPage = pages[0];
    const firstPageYOffset = 270;
    firstPage.drawImage(qrImage, {
      x: firstPage.getWidth() / 2 - QRSide / 2 - alignmentAdjustment,
      y: firstPage.getHeight() - firstPageYOffset - QRSide,
      width: QRSide,
      height: QRSide,
    });

    const pdfBytes = await pdfDoc.save();

    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    saveAs(blob, "Handy-QR.pdf");
  };

  const handleOnBranchSelectedShareLink = (branch) => {
    if (branch === "all" || branches?.length === 0) {
      return;
    }
    setSelectedShareLinkBranch(branch);
    dispatch(
      businessActions.getProvidersSubscriptionShareLinkByBranch(
        branch,
        (link) => {
          if (link) {
            setShareLink(link?.link);
          }
        }
      )
    );
  };

  const handleOnNext = () => {
    setStep(2);
  };

  const handleClose = () => {
    if (branches.length > 1) {
      setStep(1);
    }
    onClose();
  };

  return (
    <Dialog open={open} classes={{ paper: classes.dialog }}>
      <DialogTitle className={classes.dialogTitleWrapper}>
        <div className={classes.dialogTitle}>
          {i18n.get("SharePlanWithBranchesModal.Title")}
        </div>
      </DialogTitle>
      <DialogContent className={classes.dialogContentWrapper}>
        <div className={classes.contentWrapper}>
          <div className={classes.contentDetails}>
            <ContentBox
              title={i18n.get(
                step === 1
                  ? "SharePlanWithBranchesModal.ContentTitle"
                  : "SharePlanWithBranchesModal.ContentTitle2"
              )}>
              {step === 1 ? (
                <div className={classes.contentMinHeight}>
                  <div className={classes.inputWrapper}>
                    <InputLabel className={classes.selectLabel} shrink>
                      {i18n.get("SharePlanWithBranchesModal.InputLabel")}
                    </InputLabel>
                    <BranchesSelector
                      align="right"
                      fullWidth
                      branches={branches}
                      selectedBranch={selectedShareLinkBranch}
                      onBranchSelected={handleOnBranchSelectedShareLink}
                    />
                  </div>
                </div>
              ) : (
                <div className={classes.shareableContentWrapper}>
                  {shareLink && (
                    <QRCode
                      value={shareLink}
                      size={150}
                      quietZone={15}
                      logoWidth={30}
                      logoHeight={30}
                      bgColor="#fff"
                      logoImage={handyQrLogo}
                      removeQrCodeBehindLogo
                    />
                  )}
                  <div className={classes.shareableContent}>
                    <div className={classes.shareableLinkWrapper}>
                      <TextField
                        inputRef={linkInputRef}
                        variant="outlined"
                        value={shareLink}
                        contentEditable={false}
                        classes={{ root: classes.shareableLinkInput }}
                      />
                      <Button
                        className={classes.shareableLinkButton}
                        variant="contained"
                        color="primary"
                        onClick={_copyLink}>
                        {i18n.get("Components.ShareModal.CopyLinkButton")}
                      </Button>
                    </div>
                  </div>
                  <div className={classes.shareableContent}>
                    <div>
                      {i18n.get(
                        "ProvidersSubscriptionsManagePlans.ShareSinglePlanModalSocialLabel"
                      )}
                    </div>
                    <div style={{ marginTop: -3 }}>
                      <a
                        href={Constants.SHAREABLE_WHATSAPP_LINK.replace(
                          "{text}",
                          shareText ||
                            i18n.get("Components.ShareModal.ShareText")
                        ).replace("{link}", shareLink)}
                        target="_blank"
                        rel="noreferrer"
                        className={classes.socialAction}>
                        <img src={whatsAppIcon} alt="whatsappIcon" />
                      </a>
                      <a
                        href={Constants.SHAREABLE_TELEGRAM_LINK.replace(
                          "{link}",
                          shareLink
                        ).replace(
                          "{text}",
                          shareText ||
                            i18n.get("Components.ShareModal.ShareText")
                        )}
                        target="_blank"
                        rel="noreferrer"
                        className={classes.socialAction}>
                        <img src={telegramIcon} alt="telegramIcon" />
                      </a>
                      <a
                        href={Constants.SHAREABLE_EMAIL_LINK.replace(
                          "{text}",
                          shareText ||
                            i18n.get("Components.ShareModal.ShareText")
                        ).replace("{link}", shareLink)}
                        className={classes.socialAction}>
                        <img src={emailIcon} alt="emailIcon" />
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </ContentBox>
          </div>
        </div>
      </DialogContent>
      <DialogActions className={classes.dialogActionsWrapper}>
        <Button variant="outlined" onClick={handleClose}>
          {i18n.get(
            "ProvidersSubscriptionsManagePlans.TogglePlanStatusDialogActionCancel"
          )}
        </Button>
        {step === 1 && (
          <Button
            onClick={handleOnNext}
            disabled={selectedShareLinkBranch === "all"}>
            {i18n.get("SharePlanWithBranchesModal.NextButton")}
          </Button>
        )}
        {step === 2 && (
          <div className={classes.actionsWrapper}>
            {branches?.length > 1 && (
              <Button onClick={() => setStep(1)}>
                {i18n.get("SharePlanWithBranchesModal.BackButton")}
              </Button>
            )}
            <Button onClick={downloadQR}>
              {i18n.get("Components.QRModal.ButtonDownload")}
            </Button>
          </div>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SharePlanWithBranchesModal;
